/**
 * 数据报表相关
 */
import moment from 'moment';
import * as types from 'src/store/types';
import request from 'src/utils/request';
import { getAccountToday } from 'src/utils/dateUtil';
import { i18n } from 'src/utils/localeUtil';
import apis from 'src/const/apis';
import tool from 'src/utils/tool';
import * as service from 'src/service';
import {
  isRFWhite,
  isReportingTTAdsWhite,
  isTTPStatisticsWhite,
  isTTPStatisticsApiWhite,
  isAcoReportingUpgradeWhite,
  isSearchAdsMtWhite,
} from 'src/const/whiteList';
import { TIME_FORMAT, EnumLifetimeType } from 'src/const/date';
import {
  checkIsRF, campaignType, campaignObjectiveValue,
} from 'src/const/state/campaign';
import { regionType } from 'src/const/state/account';
import { creativeType } from 'src/const/state/creative';
import {
  noneFormatter,
  percentTextFormatter,
  moneyFormatterWithUnit,
  integerValueFormatter,
  percentCellFormatter,
} from 'src/const/statistics/formatters';
import { percentileDivisionFormatMoney } from 'src/mixins/currencyMixin';
import { getDaysDiff } from 'src/utils/perfUtil';
import { checkIsSearch, campaignPermitSearchEnum } from 'src/const/state/ad';
import { captureError } from '../../../logger/errorLogger';


const flattenTreeToMap = (treeData) => {
  const map = {};
  let index = 1;
  let list = treeData;
  while (list.length) {
    let newList = [];
    // eslint-disable-next-line
    list.forEach((item) => {
      if (map[index]) {
        map[index].push(item.id);
      } else {
        map[index] = [item.id];
      }
      if (item.children && Array.isArray(item.children)) {
        newList = newList.concat(item.children);
      }
    });
    list = newList;
    index++;
  }
  return map;
};

// 维护不同层级的枚举值
export const VIEW_DATA_ENUM = {
  CAMPAIGN: {
    // 请求广告基本信息
    basicInfoAPI: apis.campaign.newStatisticList,
    totalCostAPI: apis.campaign.new_total_cost,
    // 更新状态
    updateStatusAPI: apis.campaign.update_status,
    emitKey: 'campaign-toggle-status-by-id',
    // 请求 Daily 数据
    dailyReportAPI: apis.campaign.new_report_stat,
    // 数据在返回结构体中的字段名
    dailyReportKey: 'campaign_data',
    // 请求 Breakdown 数据
    breakdownReportAPI: apis.campaign.new_breakdown_report_stat,
    newBreakdownReportAPI: apis.campaign.ttp_breakdown_report_stat,
    // 数据在返回结构体中的字段名
    breakdownReportKey: 'stats',
    // 真正下载的API, 前导 API 与 Daily API 是同一个
    downloadAPI: apis.campaign.new_promote_download_url,
    newDownloadAPI: apis.campaign.ttp_promote_download_url,
    // 关键词类型，写死，见select-perf-dashboard.js/searchType
    searchType: 1,
    negativeKeywordListAPI: apis.campaign.negative_keywords_list,
    addNegativeKeywordsAPI: apis.campaign.add_negative_keywords,
    modifyNegativeKeywordsAPI: apis.campaign.modify_negative_keywords,
    deleteNegativeKeywordsAPI: apis.campaign.delete_negative_keywords,
    downloadNegativeKeywordsAPI: apis.campaign.download_negative_keywords,
  },
  AD: {
    basicInfoAPI: apis.ad.newStatisticList,
    updateStatusAPI: apis.ad.update_status,
    emitKey: 'ad-group-toggle-status-by-id',
    dailyReportAPI: apis.ad.new_report_stat,
    dailyReportKey: 'ad_data',
    breakdownReportAPI: apis.ad.new_breakdown_report_stat,
    newBreakdownReportAPI: apis.ad.ttp_breakdown_report_stat,
    breakdownReportKey: 'stats',
    downloadAPI: apis.ad.new_promote_download_url,
    newDownloadAPI: apis.ad.ttp_promote_download_url,
    searchType: 2,
    negativeKeywordListAPI: apis.ad.negative_keywords_list,
    addNegativeKeywordsAPI: apis.ad.add_negative_keywords,
    modifyNegativeKeywordsAPI: apis.ad.modify_negative_keywords,
    deleteNegativeKeywordsAPI: apis.ad.delete_negative_keywords,
    downloadNegativeKeywordsAPI: apis.ad.download_negative_keywords,
  },
  CREATIVE: {
    basicInfoAPI: apis.creative.newStatisticList,
    updateStatusAPI: apis.creative.update_status,
    emitKey: 'ad-toggle-status-by-id',
    dailyReportAPI: apis.creative.new_report_stat,
    dailyReportKey: 'creative_data',
    breakdownReportAPI: apis.creative.new_breakdown_report_stat,
    newBreakdownReportAPI: apis.creative.ttp_breakdown_report_stat,
    breakdownReportKey: 'stats',
    downloadAPI: apis.creative.new_promote_download_url,
    newDownloadAPI: apis.creative.ttp_promote_download_url,
    searchType: 3,
  },
};

// state 初始化值写在这里，方便动态生成/清理
const getInitialState = () => ({
  loading: false,
  reportId: '',
  // 层级 campaign/adgroup/ad 中的一个
  reportType: '',
  basicInfo: {},
  searchBasicInfo: {},
  // 拉取 adgroup 信息，命名与创编做以区分
  reportAdDetail: {},
  totalCost: 0,
  todayCost: 0,
  dateRange: {
    st: '',
    et: '',
    lifetime: 0,
  },
  audienceMetric: 'stat_cost',
  creativeMetric: 'stat_cost', // viewData creative tab下的metric value
  activeAudienceTab: 'genderAge',
  activeCreativeTab: 'videoImage',
  countriesWithSubRegion: [],
  usDmaIds: [], // 美国 DMA ID 数组
  usStateIds: [], // 美国 State ID 数组
  interestOptionMap: {}, // 创编的所有兴趣标签
  behaviorOptionMap: {}, // 创编的所有行为标签
  tableMetricArray: [], // 表格当前展示的数据列
  showSearchNegativeTab: false, // 处理跳转到否定关键词管理页面逻辑
});

export default {
  namespaced: true,
  state: getInitialState(),
  getters: {
    loading: state => state.loading || false,
    // 当前层级的枚举对象
    reportEnum: (state) => {
      const enumObj = VIEW_DATA_ENUM[state.reportType.toUpperCase()] || {};
      return {
        ...enumObj,
        // 白名单逻辑在这里进行覆盖
        breakdownReportAPI: enumObj[isTTPStatisticsApiWhite() ? 'newBreakdownReportAPI' : 'breakdownReportAPI'],
        downloadAPI: enumObj[isTTPStatisticsApiWhite() ? 'newDownloadAPI' : 'downloadAPI'],
      };
    },
    dateRangeDays: state => getDaysDiff(state.dateRange),
    baseInfoRequestParam: (state, getters) => {
      const { isCreativeReportType } = getters;
      return {
        keyword: '', // 关键词
        search_type: getters.reportEnum.searchType,
        campaign_status: [],
        ad_status: [],
        creative_status: [],
        objective_type: isCreativeReportType ? [] : 0,
        image_mode: isCreativeReportType ? [] : 0,
        version: 1,
        sort_stat: '',
        sort_order: 0,
        page: 1,
        pricing: [1, 2, 3, 4, 6, 7, 8, 9, 10],
        campaign_category: [2, 3, 4], // 2,3,4 表示竞价写死
        ...state.dateRange,
      };
    },
    baseRequestParam: (state, getters) => ({
      image_mode: 0, // 展现样式，写死
      compare: 0,
      creative_material_mode: 0,
      inventory_type: 0,
      version: 1,
      pricing: JSON.stringify([1, 2, 3, 4, 6, 7, 8, 9, 10]),
      campaign_category: JSON.stringify([2, 3, 4]),
      from: 'i18n_daily', // 写死，区分老平台的数据报告接口
      ...state.dateRange,
    }),
    isCampaignReportType: state => state.reportType === 'campaign',
    isAdReportType: state => state.reportType === 'ad',
    isCreativeReportType: state => state.reportType === 'creative',
    isDedicatedCampaign: (state) => {
      const { basicInfo = {} } = state;
      return basicInfo.dedicate_type === campaignType.dedicatedCampaign;
    },
    isTTPAd: (state) => {
      // 统计列表返回的 ttp 标签
      // 为后续的扩展性考虑 viewdata 模块使用列表返回的 ttp 标签
      // 用 isTTPAd 命名是为了与 account 的创编 isTTP 打标区分
      const { basicInfo = {} } = state;
      return isTTPStatisticsWhite() && +basicInfo.region_type === regionType.TTP;
    },
    audienceIframeUrl: (state, getters, rootState, rootGetters) => {
      const {
        reportType,
        dateRange,
        reportId,
      } = state;
      const {
        st,
        et,
        lifetime,
      } = dateRange;
      const query = {
        aadvid: rootState.account.aadvid,
        id: reportId,
        type: reportType,
        st,
        et,
        lifetime,
      };
      const host = !rootGetters['account/isStaffIdExist'] && getters.isTTPAd ? process.env.TTP_HOST : window.location.host;
      return `//${host}/i18n/statistics/viewdata${tool.jsonToQuery(query)}`;
    },
    upgradedTabs: (state, getters, rootState) => {
      const { basicInfo } = state;
      const tabs = {
        DAILY: {
          name: 'daily',
          key: 'data_report_daily',
          tabCompId: 'upgrade-tab-daily',
          refs: 'tabDaily',
        },
        AUDIENCE: {
          name: 'audience',
          key: 'data_report_audience',
          tooltip: 'data_report_audience_tips',
          tabCompId: 'statistics-iframe-container',
          refs: 'tabAudience',
        },
        SPLITTEST: {
          name: 'splittest',
          key: 'module_splittest',
          tooltip: '',
          tabCompId: 'data-report-tab-splittest',
          refs: 'tabSplittest',
          hide: () => !(rootState.campaign.abtestFlagForSplittest && getters.isAdReportType),
        },
        FREQUENCY: {
          name: 'frequency',
          key: 'data_report_frequency',
          tooltip: 'campaign_report_frequency_tips',
          tabCompId: 'upgrade-tab-frequency',
          refs: 'tabFrequency',
          hide: () => getters.isCampaignReportType
          || !checkIsRF(basicInfo.objective_type)
          || !isRFWhite(),
        },
        CREATIVE: {
          name: 'creative',
          key: 'data_report_creative',
          tooltip: '',
          tabCompId: 'upgrade-tab-creative',
          refs: 'tabCreative',
          hide: () => !getters.isAdReportType
          || !getters.isProcedualAd
          || !isAcoReportingUpgradeWhite(),
        },
        SEARCH: {
          name: 'search',
          key: 'module_search_ads_search_results',
          tabCompId: 'upgrade-tab-search',
          refs: 'tabSearch',
          hide: () => getters.isCreativeReportType
          || !isSearchAdsMtWhite()
          || !getters.isSearchAds, // 是否隐藏Search tab页
        },
      };
      return tabs;
    },
    isProcedualAd: state => state?.basicInfo?.creative_material_mode === creativeType.PROCEDURAL,
    isRF: state => checkIsRF(state.basicInfo.objective_type),
    isSearchAds: (state) => {
      const { basicInfo = {} } = state;
      const isAlived = basicInfo.isdel === 0; // 判断计划是否被删除
      if (basicInfo.search_delivery_type) {
        return checkIsSearch(basicInfo.search_delivery_type) && isAlived;
      }
      return isAlived && campaignPermitSearchEnum.includes(+basicInfo.objective_type);
    },
    audienceConfig: (state, getters) => ({
      GENDER_AND_AGE: {
        name: 'genderAge',
        key: 'data_report_gender_and_age',
        compId: 'gender-age-chart',
        api: getters.reportEnum?.breakdownReportAPI,
      },
      LOCATION: {
        name: 'location',
        key: 'data_report_location',
        compId: 'location-chart',
        api: getters.reportEnum?.breakdownReportAPI,
      },
      INTEREST_AND_BEHAVIOR: {
        name: 'interestBehavior',
        key: 'data_report_interest_and_behavior',
        compId: 'interest-behavior-chart',
        api: getters.reportEnum?.breakdownReportAPI,
      },
      DEVICE: {
        name: 'device',
        key: 'data_report_device',
        compId: 'device-chart',
        api: getters.reportEnum?.breakdownReportAPI,
      },
    }),
    metricsOptions: (state, getters, rootState, rootGetters) => {
      const {
        objective_type,
        dedicate_type,
        effect_note,
        effect_rate_note,
        effect_cost_note,
      } = state.basicInfo;
      // 是R&F App Install、且是DC 禁用该指标
      const rfAppInstallDcDisabled =
        (objective_type === campaignObjectiveValue.RF_APPINSTALL) && (dedicate_type === campaignType.dedicatedCampaign);
      return [
        {
          valueKey: 'stat_cost',
          name: i18n.t('common_total_cost'),
          breakdownStacking: true, // 在breakdown报告中是否显示为堆积图
          hide: false,
          valueFormatter: moneyFormatterWithUnit,
        },
        {
          valueKey: 'show_cnt',
          name: i18n.t('list_total_impression'),
          breakdownStacking: true,
          hide: false,
          valueFormatter: integerValueFormatter,
        },
        {
          valueKey: 'click_cnt',
          name: i18n.t('list_total_click'),
          breakdownStacking: true,
          hide: false,
          valueFormatter: integerValueFormatter,
        },
        {
          valueKey: 'time_attr_convert_cnt',
          name: i18n.t('list_convert_count'),
          breakdownStacking: true,
          hide: false,
          valueFormatter: integerValueFormatter,
        },
        {
          valueKey: 'skan_convert_cnt',
          name: i18n.t('list_skan_conversion'),
          breakdownStacking: true,
          hide: () => !rootGetters['dashboard/isDedicatedCampaignExist'],
          isSkan: true,
          valueFormatter: integerValueFormatter,
        },
        {
          valueKey: 'time_attr_effect_cnt',
          name: i18n.t('list_total_result'),
          breakdownStacking: true,
          hide: false,
          valueFormatter: integerValueFormatter,
          tip: effect_note,
        },
        {
          valueKey: 'ctr',
          name: i18n.t('common_CTR'),
          breakdownStacking: false,
          hide: false,
          labelFormatter: percentTextFormatter,
          valueFormatter: percentCellFormatter,
        },
        {
          valueKey: 'time_attr_conversion_rate',
          name: i18n.t('list_convert_rate'),
          breakdownStacking: false,
          hide: false,
          labelFormatter: percentTextFormatter,
          valueFormatter: percentCellFormatter,
          disabled: () => rfAppInstallDcDisabled,
        },
        {
          valueKey: 'skan_conversion_rate',
          name: i18n.t('list_skan_cvr'),
          breakdownStacking: false,
          hide: () => !rootGetters['dashboard/isDedicatedCampaignExist'],
          isSkan: true,
          labelFormatter: percentTextFormatter,
          valueFormatter: percentCellFormatter,
          disabled: () => rfAppInstallDcDisabled,
        },
        {
          valueKey: 'time_attr_effect_rate',
          name: i18n.t('list_effect_rate'),
          breakdownStacking: false,
          hide: false,
          labelFormatter: percentTextFormatter,
          valueFormatter: percentCellFormatter,
          tip: effect_rate_note,
          disabled: () => rfAppInstallDcDisabled,
        },
        {
          valueKey: 'cpc',
          name: i18n.t('common_CPC'),
          breakdownStacking: true,
          hide: false,
          valueFormatter: moneyFormatterWithUnit,
        },
        {
          valueKey: 'cpm',
          name: i18n.t('common_CPM'),
          breakdownStacking: true,
          hide: false,
          valueFormatter: moneyFormatterWithUnit,
        },
        {
          valueKey: 'time_attr_conversion_cost',
          name: i18n.t('list_convert_cost'),
          breakdownStacking: true,
          hide: false,
          valueFormatter: moneyFormatterWithUnit,
          disabled: () => rfAppInstallDcDisabled,
        },
        {
          valueKey: 'skan_conversion_cost',
          name: i18n.t('list_skan_cpa'),
          breakdownStacking: true,
          hide: () => !rootGetters['dashboard/isDedicatedCampaignExist'],
          isSkan: true,
          valueFormatter: moneyFormatterWithUnit,
          disabled: () => rfAppInstallDcDisabled,
        },
        {
          valueKey: 'time_attr_effect_cost',
          name: i18n.t('list_effect_cost'),
          breakdownStacking: true,
          hide: false,
          valueFormatter: moneyFormatterWithUnit,
          tip: effect_cost_note,
          disabled: () => rfAppInstallDcDisabled,
        },
        {
          valueKey: 'show_uv',
          name: i18n.t('list_video_show_uv'),
          breakdownStacking: true,
          hint: i18n.t('campaign_hover_stat_data.show_uv'),
          hide: false,
          isOnlySupportDaliy: true, // 不支持audience
          isRFMetrics: true,
          valueFormatter: integerValueFormatter,
        },
        {
          valueKey: 'cost_per_show_uv_mille',
          name: i18n.t('list_video_cost_per_show_uv_mille'),
          breakdownStacking: true,
          hint: i18n.t('campaign_hover_stat_data.cost_per_show_uv_mille'),
          hide: false,
          isOnlySupportDaliy: true, // 不支持audience
          isRFMetrics: true,
          valueFormatter: moneyFormatterWithUnit,
        },
        {
          valueKey: 'frequency',
          name: i18n.t('common_frequency'),
          breakdownStacking: true,
          hint: i18n.t('common_frequency_report_hint'),
          hide: false,
          isRFMetrics: true,
          isOnlySupportDaliy: true, // 不支持audience
          valueFormatter: noneFormatter,
        },
      ];
    },
    creativeMetricsOptions: (state, getters, rootState, rootGetters) => [
      {
        valueKey: 'stat_cost',
        name: i18n.t('common_total_cost'),
        hide: false,
        valueFormatter: moneyFormatterWithUnit,
      },
      {
        valueKey: 'time_attr_effect_cost',
        name: i18n.t('list_effect_cost'),
        hide: false,
        valueFormatter: moneyFormatterWithUnit,
        disabled: () => state.basicInfo.dedicate_type === campaignType.dedicatedCampaign,
      },
      {
        valueKey: 'time_attr_effect_cnt',
        name: i18n.t('list_effect_count'),
        hide: false,
        valueFormatter: integerValueFormatter,
        disabled: () => state.basicInfo.dedicate_type === campaignType.dedicatedCampaign,
      },
      {
        valueKey: 'click_cnt',
        name: i18n.t('list_total_click'),
        hide: false,
        valueFormatter: integerValueFormatter,
      },
      {
        valueKey: 'show_cnt',
        name: i18n.t('list_total_impression'),
        hide: false,
        valueFormatter: integerValueFormatter,
      },
      {
        valueKey: 'ctr',
        name: i18n.t('common_CTR'),
        hide: false,
        valueFormatter: percentCellFormatter,
      },
      {
        valueKey: 'time_attr_conversion_rate',
        name: i18n.t('list_convert_rate'),
        hide: false,
        valueFormatter: percentCellFormatter,
        disabled: () => state.basicInfo.dedicate_type === campaignType.dedicatedCampaign,
      },
    ],
    isSkanHide(state, getters, rootState, rootGetters) {
      return !rootGetters['dashboard/isDedicatedCampaignExist'];
    },
    dailyTargetConfig(state, getters) {
      const { stat_data = {} } = state.basicInfo;
      const targetList = [
        {
          label: i18n.t('common_total_cost'),
          key: 'stat_cost',
          value: percentileDivisionFormatMoney(stat_data.stat_cost),
          showCurrencyCode: true,
        },
        {
          label: i18n.t('list_convert_cost'),
          key: 'time_attr_conversion_cost',
          value: percentileDivisionFormatMoney(
            stat_data.time_attr_conversion_cost,
          ),
          showCurrencyCode: true,
        },
        {
          label: i18n.t('list_skan_cpa'),
          key: 'skan_conversion_cost',
          value: percentileDivisionFormatMoney(stat_data.skan_conversion_cost),
          showCurrencyCode: true,
          hide: getters.isSkanHide,
        },
        {
          label: i18n.t('common_CPC'),
          key: 'cpc',
          value: percentileDivisionFormatMoney(stat_data.cpc),
          showCurrencyCode: true,
        },
        {
          label: i18n.t('data_report_impression'),
          key: 'show_cnt',
          value: integerValueFormatter('', '', stat_data.show_cnt),
        },
        {
          label: i18n.t('common_CTR'),
          key: 'ctr',
          value: percentCellFormatter('', '', stat_data.ctr),
        },
        {
          label: i18n.t('data_report_dimensions_index_click_cnt'),
          key: 'click_cnt',
          value: integerValueFormatter('', '', stat_data.click_cnt),
        },
        {
          label: i18n.t('list_convert_rate'),
          key: 'time_attr_conversion_rate',
          value: percentCellFormatter('', '', stat_data.time_attr_conversion_rate),
        },
        {
          label: i18n.t('list_skan_cvr'),
          key: 'skan_conversion_rate',
          value: percentCellFormatter('', '', stat_data.skan_conversion_rate),
          hide: getters.isSkanHide,
        },
        {
          label: i18n.t('list_convert_count'),
          key: 'time_attr_convert_cnt',
          value: integerValueFormatter('', '', stat_data.time_attr_convert_cnt),
        },
        {
          label: i18n.t('list_skan_conversion'),
          key: 'skan_convert_cnt',
          value: integerValueFormatter('', '', stat_data.skan_convert_cnt),
          hide: getters.isSkanHide,
        },
      ];
      return targetList.filter(item => !item.hide);
    },
    allDailyTargetConfig(state, getters) {
      const {
        stat_data = {},
        effect_note,
        effect_cost_note,
        effect_rate_note,
      } = state.basicInfo;

      const targetList = [
        {
          label: i18n.t('list_total_result'), // 成效数
          key: 'time_attr_effect_cnt',
          value: integerValueFormatter('', '', stat_data.time_attr_effect_cnt),
          tip: effect_note,
        },
        {
          label: i18n.t('list_effect_cost'),
          key: 'time_attr_effect_cost',
          value: percentileDivisionFormatMoney(stat_data.time_attr_effect_cost),
          tip: effect_cost_note,
          showCurrencyCode: true,
        },
        {
          label: i18n.t('list_effect_rate'), // 成效率
          key: 'time_attr_effect_rate',
          value: percentCellFormatter('', '', stat_data.time_attr_effect_rate),
          tip: effect_rate_note,
        },
        {
          label: i18n.t('common_CPM'),
          key: 'cpm',
          value: percentileDivisionFormatMoney(stat_data.cpm),
          showCurrencyCode: true,
        },
        {
          label: i18n.t('common_frequency'), // frequency
          key: 'frequency',
          value: noneFormatter('', '', stat_data.frequency),
        },
        {
          label: i18n.t('list_video_show_uv'), // 总覆盖UV
          key: 'show_uv',
          value: integerValueFormatter('', '', stat_data.show_uv),
        },
        {
          label: i18n.t('common_in_app_event_active_count'), // App Install
          key: 'active',
          value: integerValueFormatter('', '', stat_data.active),
        },
        {
          label: i18n.t('list_skan_event_app_install_unique_count'),
          key: 'skan_active',
          value: integerValueFormatter('', '', stat_data.skan_active),
          hide: getters.isSkanHide,
        },
        {
          label: i18n.t('common_in_app_event_time_attr_cost_per_active'),
          key: 'time_attr_active_cost',
          value: percentileDivisionFormatMoney(stat_data.time_attr_active_cost),
          showCurrencyCode: true,
        },
        {
          label: i18n.t('list_skan_event_app_install_unique_cost'),
          key: 'cost_per_skan_active',
          value: percentileDivisionFormatMoney(stat_data.cost_per_skan_active),
          showCurrencyCode: true,
          hide: getters.isSkanHide,
        },
        {
          label: i18n.t('list_skan_event_purchase_unique_count'),
          key: 'skan_active_pay',
          value: integerValueFormatter('', '', stat_data.skan_active_pay),
          hide: getters.isSkanHide,
        },
        {
          label: i18n.t('list_skan_event_purchase_each_value'),
          key: 'skan_total_repetitive_active_pay_value',
          value: percentileDivisionFormatMoney(stat_data.skan_total_repetitive_active_pay_value),
          showCurrencyCode: true,
          hide: getters.isSkanHide,
        },
        {
          label: i18n.t('common_in_app_event_time_attr_total_repetitive_active_pay_value'),
          key: 'time_attr_total_repetitive_active_pay_value',
          value: percentileDivisionFormatMoney(stat_data.time_attr_total_repetitive_active_pay_value),
          showCurrencyCode: true,
        },
        {
          label: i18n.t('common_in_app_event_time_attr_active_pay_count'),
          key: 'time_attr_active_pay',
          value: integerValueFormatter('', '', stat_data.time_attr_active_pay),
        },
        {
          labe: i18n.t('common_in_app_event_roas'), // ROAS
          key: 'time_attr_repetitive_active_pay_roas',
          value: integerValueFormatter('', '', stat_data.time_attr_repetitive_active_pay_roas),
        },
      ];
      return getters.dailyTargetConfig.concat(targetList.filter(item => !item.hide));
    },
    // Search terms表格列配置
    searchTargetConfig(state, getters) {
      const stat_data = state.searchBasicInfo;
      const targetList = [
        {
          label: i18n.t('common_total_cost'),
          key: 'stat_cost',
          value: percentileDivisionFormatMoney(stat_data.stat_cost),
          showCurrencyCode: true,
        },
        {
          label: i18n.t('common_CPC'),
          key: 'cpc',
          value: percentileDivisionFormatMoney(stat_data.cpc),
          showCurrencyCode: true,
        },
        {
          label: i18n.t('common_Click'),
          key: 'click_cnt',
          value: integerValueFormatter('', '', stat_data.click_cnt),
        },
        {
          label: i18n.t('common_CPM'),
          key: 'cpm',
          value: percentileDivisionFormatMoney(stat_data.cpm),
          showCurrencyCode: true,
        },
        {
          label: i18n.t('common_CTR'),
          key: 'ctr',
          value: percentCellFormatter('', '', stat_data.ctr),
        },
        {
          label: i18n.t('data_report_impression'),
          key: 'show_cnt',
          value: integerValueFormatter('', '', stat_data.show_cnt),
        },
      ];
      return targetList.filter(item => !item.hide);
    },
  },
  mutations: {
    [types.SET_TABLE_METRIC_ARRAY](state, data) {
      state.tableMetricArray = data;
    },
    [types.SET_REPORT_ID](state, data) {
      state.reportId = data;
    },
    [types.SET_REPORT_TYPE](state, data) {
      state.reportType = data;
    },
    [types.SET_REPORT_INFO](state, data) {
      state.reportId = data.id;
      state.reportType = data.type;
    },
    [types.SET_BASIC_INFO](state, data) {
      state.basicInfo = data;
    },
    [types.SET_SEARCH_BASIC_INFO](state, data) {
      state.searchBasicInfo = data;
    },
    [types.SET_REPORT_AD_DETAIL](state, data) {
      state.reportAdDetail = data;
    },
    [types.SET_TOTAL_COST](state, data) {
      state.totalCost = data;
    },
    [types.SET_TODAY_COST](state, data) {
      state.todayCost = data;
    },
    [types.SET_DATE_RANGE](state, data) {
      state.dateRange = data;
    },
    [types.SET_AUDIENCE_METRIC](state, data) {
      state.audienceMetric = data;
    },
    [types.SET_AUDIENCE_TAB](state, data) {
      state.activeAudienceTab = data;
    },
    [types.SET_ALL_COUNTRIES](state, { countries, usDmaIds, usStateIds }) {
      state.countriesWithSubRegion = countries;
      state.usDmaIds = usDmaIds;
      state.usStateIds = usStateIds;
    },
    [types.SET_INTEREST_MAP](state, data) {
      state.interestOptionMap = data;
    },
    [types.SET_BEHAVIOR_MAP](state, data) {
      state.behaviorOptionMap = data;
    },
    [types.SET_LOADING_STATE](state, { status }) {
      state.loading = status;
    },
    [types.SET_SHOW_NEGATIVE_TAB](state, status) {
      state.showSearchNegativeTab = status;
    },
    [types.CLEAR_REPORT_STATE](state) {
      const initialState = getInitialState();
      Object.keys(state).forEach((key) => {
        state[key] = initialState[key];
      });
    },
  },
  actions: {
    // ViewData 获取广告 basic info
    async getBasicInfo({
      commit, dispatch, state, getters,
    }, id) {
      let basicInfo = null;
      try {
        const api = getters.reportEnum.basicInfoAPI;
        const queryList = getters.isCampaignReportType
          ? getters.dailyTargetConfig.map(item => item.key)
          : getters.allDailyTargetConfig.map(item => item.key);
        const resp = await request.post(api, {
          ...getters.baseInfoRequestParam,
          ...state.dateRange,
          [`${state.reportType}_ids`]: [id],
          query_list: queryList,
        }) || {};
        const { code, data = null } = resp;
        if (data && code === 0) {
          basicInfo = data.table[0] || null;
          if (getters.isAdReportType) {
            await dispatch('getAdTypeByDetail', id);
          }
          if (getters.isCreativeReportType && basicInfo?.ad_id) {
            await dispatch('getAdTypeByDetail', basicInfo.ad_id);
          }
        }
      } catch (err) {
        captureError(err);
      }
      commit(types.SET_BASIC_INFO, basicInfo || {});
      return basicInfo;
    },
    // 获取Search表格概览汇总数据
    async getSearchBasicInfo({
      commit, state, getters,
    }, id) {
      let searchBasicInfo = {};
      try {
        const api = getters.reportEnum.newBreakdownReportAPI;
        const queryList = getters.searchTargetConfig.map(item => item.key);
        const resp = await request.post(api, {
          ...getters.baseInfoRequestParam,
          ...state.dateRange,
          ids: id,
          query_list: queryList.join(','),
          dims: 'ad_position_type',
          ad_position_type: 2,
          is_chart: '0',
        }) || {};
        const { code, data = {} } = resp;
        if (data && code === 0) {
          searchBasicInfo = data.stats[0] || {};
        }
      } catch (err) {
        captureError(err);
      }
      commit(types.SET_SEARCH_BASIC_INFO, searchBasicInfo || {});
      return searchBasicInfo;
    },
    async getAdTypeByDetail({ commit }, id) {
      let adgroupInfo = {};
      try {
        const { data } = await service.getAdgroupInfo({ ad_id: id });
        adgroupInfo = {
          ad_id: id,
          ...data.base_info,
        };
      } catch (err) {
        captureError(err);
      }
      commit(types.SET_REPORT_AD_DETAIL, adgroupInfo);
      return adgroupInfo;
    },
    async getTodayCost({ commit, state, getters }, id) {
      let todayCost = 0;
      try {
        const api = getters.reportEnum.basicInfoAPI;
        const mToday = moment(getAccountToday()).format(TIME_FORMAT);
        const params = {
          ...getters.baseInfoRequestParam,
          st: mToday,
          et: mToday,
          lifetime: EnumLifetimeType.unchosen, // st、et为同一天，则lifetime默认为0
          [`${state.reportType}_ids`]: [id],
          query_list: ['stat_cost'],
        };
        const { data } = await request.post(api, params);
        const info = data.table[0] || {};
        const statData = info.stat_data || {};
        todayCost = Number(statData.stat_cost) || 0;
      } catch (err) {
        captureError(err);
      }
      commit(types.SET_TODAY_COST, todayCost);
      return todayCost;
    },
    async getTotalCost({ commit, state, getters }, id) {
      let totalCost = 0;
      try {
        const api = getters.reportEnum.totalCostAPI;
        const { data } = await request.get(api, {
          ids: id,
          category: `${state.reportType}_id`,
        });
        const { metrics = {} } = data.data[0] || {};
        totalCost = Number(metrics.total_cost) || 0;
      } catch (err) {
        captureError(err);
      }
      commit(types.SET_TOTAL_COST, totalCost);
      return totalCost;
    },
    async getAllCountries({ commit, dispatch }) {
      let list = [];
      let usDmaIds = [];
      let usStateIds = [];
      const param = { inventory_flow: JSON.stringify([3000, 1000, 9000]) };
      try {
        list = await dispatch('campaign/adgroup/loadAreasData', param, { root: true });
        // 过滤出有 sub region 的国家
        list = list.filter(country => country.children && country.children.length);
        if (list && list.length) {
          const USItem = list.find(item => item.code === 'US');
          if (USItem) {
            usDmaIds = USItem.dma.map(item => item.id);
            usStateIds = USItem.children.map(item => item.id);
          }
        }
      } catch (err) {
        captureError(err);
      }
      commit(types.SET_ALL_COUNTRIES, { countries: list, usDmaIds, usStateIds });
      return list;
    },
    async getInterestOptions({ commit, state }) {
      let interestMap = {};
      try {
        const inventory = JSON.stringify([3000, 1000, 9000]);
        const resp = await request.get(apis.interestOption, { inventory });
        const { ad_interest_list } = resp.data;
        interestMap = flattenTreeToMap(ad_interest_list);
      } catch (err) {
        captureError(err);
      }
      commit(types.SET_INTEREST_MAP, interestMap);
      return interestMap;
    },
    async getBehaviorOptions({ commit, state }) {
      let behaviorMap = {};
      try {
        const resp = await request.get(apis.ad.get_action_categort);
        const { creator_related, video_related } = resp.data;
        behaviorMap = flattenTreeToMap(creator_related.concat(video_related));
      } catch (err) {
        captureError(err);
      }
      commit(types.SET_BEHAVIOR_MAP, behaviorMap);
      return behaviorMap;
    },
    // 获取查询模板列表
    async getTemplatesList({ commit, state }, params = {}) {
      let data = {};
      try {
        commit(types.SET_LOADING_STATE, { status: true });
        const aipUrl = isReportingTTAdsWhite()
          ? apis.reporting.new_get_templates
          : apis.reporting.get_templates;
        const resp = await request.get(aipUrl, params);
        if (resp && resp.data && resp.code === 0) {
          data = resp.data || {};
        }
      } catch (err) {
        captureError(err);
      }
      commit(types.SET_LOADING_STATE, { status: false });
      return data;
    },
    async switchStatus({ state, getters }, { operation, id }) {
      let resp = null;
      try {
        const api = getters.reportEnum.updateStatusAPI;
        resp = await request.postForm(api, {
          operation,
          [`${state.reportType}_list`]: JSON.stringify([id]),
        }) || {};
      } catch (err) {
        captureError(err);
      }
      return resp;
    },
    // 获取 当前 adgroup下的 创意素材信息
    async getMaterialDetail({ state, getters }) {
      let materialTableInfo = [];
      try {
        const api = apis.ad.getMaterialStat;
        const { data } = await request.get(api, {
          ad_id: state.reportId,
          st: state.dateRange.st,
          et: state.dateRange.et,
        });
        materialTableInfo = data;
      } catch (err) {
        captureError(err);
      }
      return materialTableInfo;
    },
  },
};
